import type { ReactNode } from 'react';
import type { NavGroup } from '../../components/layout';

/** Line-style SVG icon (24px stroke grid) so every admin nav icon renders consistently. */
function icon(node: ReactNode): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {node}
    </svg>
  );
}

/** Shared sidebar navigation for every admin page (single source of truth). */
export const ADMIN_NAV: NavGroup[] = [
  {
    section: 'Overview',
    active: 'admin',
    items: [
      {
        to: '/admin',
        label: 'Dashboard',
        icon: icon(
          <>
            <rect x="3" y="3" width="7" height="9" rx="1" />
            <rect x="14" y="3" width="7" height="5" rx="1" />
            <rect x="14" y="12" width="7" height="9" rx="1" />
            <rect x="3" y="16" width="7" height="5" rx="1" />
          </>,
        ),
      },
      {
        to: '/admin/monitoring',
        label: 'Monitoring',
        icon: icon(
          <>
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </>,
        ),
      },
    ],
  },
  {
    section: 'Reports',
    active: 'admin',
    items: [
      {
        to: '/admin/reports',
        label: 'Overview',
        icon: icon(
          <>
            <line x1="12" y1="20" x2="12" y2="10" />
            <line x1="18" y1="20" x2="18" y2="4" />
            <line x1="6" y1="20" x2="6" y2="16" />
          </>,
        ),
      },
      {
        to: '/admin/reports/transactions',
        label: 'Transactions',
        icon: icon(
          <>
            <path d="M4 4h16v16H4z" />
            <path d="M4 10h16" />
            <path d="M9 15h6" />
          </>,
        ),
      },
      {
        to: '/admin/reports/subscriptions',
        label: 'Subscriptions',
        icon: icon(
          <>
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
            <path d="M6 15h4" />
          </>,
        ),
      },
      {
        to: '/admin/reports/streams',
        label: 'Streams',
        icon: icon(
          <>
            <path d="m22 8-6 4 6 4V8Z" />
            <rect x="2" y="6" width="14" height="12" rx="2" />
          </>,
        ),
      },
      {
        to: '/admin/reports/audit-log',
        label: 'Audit Log',
        icon: icon(
          <>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M9 13h6M9 17h4" />
          </>,
        ),
      },
    ],
  },
  {
    section: 'Manage',
    active: 'admin',
    items: [
      {
        to: '/admin/tenants',
        label: 'Tenants',
        icon: icon(
          <>
            <path d="M6 22V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v18" />
            <path d="M3 22h18" />
            <path d="M9 7h1M9 11h1M9 15h1M14 7h1M14 11h1M14 15h1" />
          </>,
        ),
      },
      {
        to: '/admin/recordings',
        label: 'Recordings',
        icon: icon(
          <>
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="3" />
          </>,
        ),
      },
      {
        to: '/admin/plans',
        label: 'Plans',
        icon: icon(
          <>
            <path d="M6 3h12l4 6-10 13L2 9z" />
            <path d="M11 3 8 9l4 13 4-13-3-6" />
            <path d="M2 9h20" />
          </>,
        ),
      },
      {
        to: '/admin/settings',
        label: 'Settings',
        icon: icon(
          <>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </>,
        ),
      },
      {
        to: '/admin/users',
        label: 'Admin Users',
        icon: icon(
          <>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </>,
        ),
      },
      {
        to: '/admin/profile',
        label: 'Profile',
        icon: icon(
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>,
        ),
      },
    ],
  },
  {
    section: 'Chat',
    active: 'admin',
    items: [
      {
        to: '/admin/chat',
        label: 'Conversations',
        icon: icon(
          <>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </>,
        ),
      },
    ],
  },
];